import React, { useEffect, useMemo, useState } from 'react';
import { AlertBanner, Button, Input, SegmentedControl, SparkIcon, TooltipTrigger } from '@spark';

import { useAuth } from '@/features/auth/AuthContext';
import '../settings.css';

// Alias is derived from auth mode — not user-selectable.
// same_subscription → dev, cross_subscription → prod (max one target per alias).
function aliasForAuthMode(authMode) {
    return authMode === 'cross_subscription' ? 'prod' : 'dev';
}

// Azure Container Registry login server and repository URI regex
const ACR_REPOSITORY_URI_RE =
    /^[a-zA-Z0-9-]+\.azurecr\.io\/[a-zA-Z0-9][a-zA-Z0-9._/-]*$/;

function validateAcrRepositoryUri(uri) {
    const trimmed = (uri || '').trim();
    if (!trimmed) return 'ACR repository URI is required';
    if (!ACR_REPOSITORY_URI_RE.test(trimmed)) {
        return 'Use <registry-name>.azurecr.io/<repository>[/<image>]';
    }
    return null;
}

function normalizeTargets(targets) {
    return (targets || []).map((t) => {
        const auth_mode = t.auth_mode || 'same_subscription';
        return { uri: t.uri || '', auth_mode, alias: aliasForAuthMode(auth_mode) };
    });
}

function targetsEqual(a, b) {
    if (a.length !== b.length) return false;
    return a.every((t, i) => {
        const o = b[i];
        return (
            (t.uri || '') === (o.uri || '') &&
            (t.auth_mode || '') === (o.auth_mode || '') &&
            (t.alias || '') === (o.alias || '')
        );
    });
}

const AUTH_MODE_ITEMS = [
    { id: 'same_subscription', label: 'Same subscription' },
    { id: 'cross_subscription', label: 'Cross subscription' },
];

/**
 * ACR / Azure Container Registry push targets (deployment) settings —
 * Spark rebuild.
 *
 * Both auth modes use the platform pod's Azure Workload Identity (UAMI) via
 * DefaultAzureCredential — no credentials stored.
 */
const AcrCredentialsForm = ({ settings, onAcrSettingsUpdate }) => {
    const { role, isAuthEnabled } = useAuth();
    const isAdmin = !isAuthEnabled || role === 'Admin';

    const [targets, setTargets] = useState(normalizeTargets(settings?.azure_acr_targets));
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setTargets(normalizeTargets(settings?.azure_acr_targets));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(settings?.azure_acr_targets || [])]);

    const savedTargets = useMemo(
        () => normalizeTargets(settings?.azure_acr_targets),
        [settings?.azure_acr_targets]
    );

    const isDirty = !targetsEqual(targets, savedTargets);

    const targetErrors = targets.map((t) => validateAcrRepositoryUri(t.uri));
    const hasTargetErrors = targetErrors.some(Boolean);

    const hasSameSubscription = targets.some((t) => t.auth_mode === 'same_subscription');
    const hasCrossSubscription = targets.some((t) => t.auth_mode === 'cross_subscription');

    const handleTargetChange = (index, key, value) => {
        setTargets((prev) =>
            prev.map((t, i) => {
                if (i !== index) return t;
                const updated = { ...t, [key]: value };
                if (key === 'auth_mode') updated.alias = aliasForAuthMode(value);
                return updated;
            })
        );
    };

    const handleAddTarget = (authMode) => {
        setTargets((prev) => [
            ...prev,
            { uri: '', auth_mode: authMode, alias: aliasForAuthMode(authMode) },
        ]);
    };

    const handleRemoveTarget = (index) => {
        setTargets((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        if (hasTargetErrors) return;
        const fields = {
            azure_acr_targets: targets.map((t) => {
                const auth_mode = t.auth_mode || 'same_subscription';
                return { uri: (t.uri || '').trim(), auth_mode, alias: aliasForAuthMode(auth_mode) };
            }),
        };
        setIsSaving(true);
        onAcrSettingsUpdate(fields, 'saved');
        setTimeout(() => setIsSaving(false), 500);
    };

    return (
        <div className="settings-providers__panel">
            <AlertBanner
                feedback="info"
                showIcon
                showButton={false}
                showCloseButton={false}
                text="This feature is currently in development and will be available soon."
            />

            <p className="settings-text settings-providers__description fs-unmask">
                Add ACR repositories. Each target is automatically aliased:
                same-subscription → dev, cross-subscription → prod. Both modes
                use the platform pod&apos;s Azure Workload Identity (UAMI) - no
                credentials stored.
            </p>

            <h3 className="settings-subheading fs-unmask">Targets</h3>

            {targets.length === 0 ? (
                <p className="settings-text fs-unmask">
                    No ACR targets configured yet. Add one to enable
                    &quot;Push to Azure ACR&quot;.
                </p>
            ) : null}

            {targets.map((target, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className="settings-target-row">
                    <div className="settings-target-row__field">
                        <Input
                            size="large"
                            showLabel={false}
                            value={target.uri}
                            onChange={(e) => handleTargetChange(index, 'uri', e.target.value)}
                            placeholder="myregistry.azurecr.io/specificai-inference/my-model"
                            disabled={!isAdmin}
                            className="settings-secret__input"
                        />
                        {targetErrors[index] ? (
                            <p className="settings-field-error">{targetErrors[index]}</p>
                        ) : null}
                    </div>
                    <TooltipTrigger
                        text={
                            target.auth_mode === 'same_subscription'
                                ? 'Uses platform Azure Workload Identity - no credentials needed'
                                : 'Registry lives in another subscription - customer grants AcrPush to the platform UAMI'
                        }
                    >
                        <span>
                            <SegmentedControl
                                size="small"
                                items={AUTH_MODE_ITEMS.map((item) => ({
                                    ...item,
                                    disabled: !isAdmin,
                                }))}
                                activeId={target.auth_mode}
                                onChange={(value) => handleTargetChange(index, 'auth_mode', value)}
                            />
                        </span>
                    </TooltipTrigger>
                    <span className="settings-target-row__alias">{target.alias}</span>
                    <Button
                        size="tiny"
                        variant="ghost"
                        iconSlug="trash"
                        aria-label="Remove target"
                        startIcon={<SparkIcon slug="trash" size="small" />}
                        onClick={() => handleRemoveTarget(index)}
                        disabled={!isAdmin}
                    />
                </div>
            ))}

            <div className="settings-form__row">
                {!hasSameSubscription ? (
                    <Button
                        size="tiny"
                        variant="ghost"
                        showStartIcon
                        onClick={() => handleAddTarget('same_subscription')}
                        disabled={!isAdmin}
                    >
                        Add Dev Target
                    </Button>
                ) : null}
                {!hasCrossSubscription ? (
                    <Button
                        size="tiny"
                        variant="ghost"
                        showStartIcon
                        onClick={() => handleAddTarget('cross_subscription')}
                        disabled={!isAdmin}
                    >
                        Add Prod Target
                    </Button>
                ) : null}
            </div>

            <h3 className="settings-subheading fs-unmask">Cross-subscription setup</h3>
            <p className="settings-text settings-providers__description fs-unmask">
                Only used for cross-subscription targets: in your Azure
                subscription, grant AcrPush on the target registry to the
                platform&apos;s managed identity (UAMI). No credentials are
                stored - the platform uses its own Azure Workload Identity at
                push time.
            </p>

            <div className="settings-form__footer">
                <Button
                    size="tiny"
                    variant="primary"
                    onClick={handleSave}
                    disabled={!isAdmin || !isDirty || hasTargetErrors || isSaving}
                    disableWhenReadOnly
                >
                    Save Changes
                </Button>
            </div>
        </div>
    );
};

export default AcrCredentialsForm;
